/*
 * Everything the navbar knows about *what* to render lives here, so the
 * components below only deal with *how* to render it.
 *
 * Labels are functions of the translation dictionary rather than plain strings
 * because the dictionary is swapped wholesale when the locale changes.
 */

/*
 * The audience the visitor is browsing as. The values are the Arabic strings
 * that were already persisted in the redux `mainTab` slice and are read by
 * several pages (Testimonialcard, /products/insurance, /sarwa-life, ...), so
 * they must stay exactly as they are.
 */
export const AUDIENCE = {
  individuals: "افراد",
  companies: "شركات",
};

export const AUDIENCE_TABS = [
  {
    id: AUDIENCE.individuals,
    href: "/",
    label: (t) => t.main_nav.individuals,
  },
  {
    id: AUDIENCE.companies,
    href: "/companies",
    label: (t) => t.main_nav.companies,
  },
];

export const CATALOGUE_URL =
  "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/Contact-Catalogue.pdf";

export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=eg.contact";

export const PARTNERSHIP_URL = "/partnership/leasing-partners";

/* Once the visitor is already on one of the partner forms the CTA is inert. */
export const PARTNERSHIP_FORM_PATHS = new Set([
  // "/partnership/contact-now-merchant",
  "/partnership/leasing-partners",
  "/partnership/factoring-partners",
  "/partnership/business-partners",
]);

/*
 * The two entry points to Investor Relations have always pointed at different
 * URLs: the desktop menu links to the site root for English, the drawer links
 * to an explicit locale segment. Kept as-is — only the marketing team can say
 * which of the two the site should settle on.
 */
export const investorRelationsMenuUrl = (locale) =>
  locale === "en"
    ? "https://investorrelations.contact.eg/"
    : "https://investorrelations.contact.eg/ar";

export const investorRelationsDrawerUrl = (locale) =>
  `https://investorrelations.contact.eg/${locale}`;

/* Products that belong to the companies experience instead of individuals. */
const COMPANY_PRODUCT_NAMES = new Set(["factoring", "leasing"]);

export const isCompanyProduct = (product) =>
  COMPANY_PRODUCT_NAMES.has(product?.name_en?.toLowerCase()?.trim() || "");

export const ICONS = {
  financial: "/sideDrawer/financial.svg",
  insurance: "/sideDrawer/insurance.svg",
  payment: "/sideDrawer/payment.svg",
  blogs: "/sideDrawer/blogs_icon.svg",
  investment: "/sideDrawer/investement.svg",
  partnership: "/sideDrawer/contct programs.svg",
  profile: "/sideDrawer/profile.svg",
  installment: "/sideDrawer/installement.svg",
  call: "/sideDrawer/call.svg",
  marker:
    "https://image-solution-no-scale.s3.us-east-2.amazonaws.com/upload/marker.png",
  uaeFlag:
    "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/uae-flag.svg",
  contactNow:
    "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/brand-c-contact-now.webp",
  /* The Contact wordmark exists as one file per locale. */
  brand: (locale) => `/sideDrawer/Contact_${locale}.png`,
  menuArrow: (locale) =>
    locale === "en" ? "/sideDrawer/arr-r.svg" : "/sideDrawer/arr-l.svg",
};

export const MENU_DIVIDER_IMAGE =
  "https://image-solution-no-scale.s3.us-east-2.amazonaws.com/upload/line.png";

export const PRODUCTS_MENU_DIVIDER_IMAGE =
  "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/line-main.webp";

/* ------------------------------------------------------------------ *
 * Desktop navigation
 * ------------------------------------------------------------------ */

/*
 * NOTE: `secondary_nav.investment_and_savings` does not exist in either
 * dictionary (it is commented out in locales/ar.js), so this entry has always
 * rendered an empty link. It is kept because removing it would drop a flex gap
 * and shift the row; the fix belongs in the dictionaries, not here.
 */
export const INDIVIDUAL_LINKS = [
  {
    id: "contact-now",
    href: "/products/contact-now",
    label: (t) => t.secondary_nav.contact_now,
  },
  {
    id: "investment",
    href: "/investment",
    label: (t) => t.secondary_nav.investment_and_savings,
  },
  {
    id: "e-payment",
    href: "/e-payment",
    label: (t) => t.secondary_nav.e_payments,
  },
  {
    id: "catalogue",
    href: CATALOGUE_URL,
    external: true,
    label: (t) => t.secondary_nav.clients_program,
  },
  {
    id: "offers",
    href: "/offers",
    label: (t) => t.secondary_nav.offers,
  },
  {
    id: "branches",
    href: "/our-branches",
    icon: ICONS.marker,
    label: (t) => t.secondary_nav.branches_location,
  },
];

export const COMPANY_LINKS = [
  {
    id: "business-investment",
    href: "/buisness-investment",
    label: (t) => t.navbar_investment,
  },
];

/* ------------------------------------------------------------------ *
 * "Menu" dropdown (the hamburger in the desktop top bar)
 * ------------------------------------------------------------------ */

export const MAIN_MENU_LINKS = [
  { id: "about", href: () => "/about", label: (t) => t.general.menu.about_us },
  {
    id: "why-contact",
    href: () => "/why-contact",
    label: (t) => t.general.menu.why_contact,
  },
  {
    id: "our-brands",
    href: () => "/our-brands",
    label: (t) => t.general.menu.our_brand,
  },
  {
    id: "contact-uae",
    href: () => "/contact-uae",
    label: (t) => t.general.menu.contact_uae,
  },
  {
    id: "contact-us",
    href: () => "/contact-us",
    label: (t) => t.general.menu.contact_us,
  },
  {
    id: "payment-channels",
    href: () => "/payment-channels",
    label: (t) => t.general.menu.payment_channels,
  },
  { id: "blogs", href: () => "/blogs", label: (t) => t.general.menu.blog },
  {
    id: "careers",
    href: () => "/careers",
    label: (t) => t.general.menu.careers,
  },
  {
    id: "sustainability",
    href: () => "/sustainability",
    label: (t) => t.general.menu.sustainability,
  },
  {
    id: "investor-relations",
    href: investorRelationsMenuUrl,
    label: (t) => t.general.menu.investor_relations,
  },
];

/* ------------------------------------------------------------------ *
 * Mobile drawer
 * ------------------------------------------------------------------ */

/*
 * `section` entries expand in place (their content is rendered by
 * MobileDrawer); the rest navigate. `external` opens in a new tab.
 */
export const DRAWER_INDIVIDUAL_ITEMS = [
  {
    id: "financial",
    section: "financial",
    icon: () => ICONS.financial,
    label: (t) => t.secondary_nav.financial_services,
  },
  {
    id: "insurance",
    section: "insurance",
    icon: () => ICONS.insurance,
    label: (t) => t.secondary_nav.insurance_services,
  },
  {
    id: "contact-uae",
    href: () => "/contact-uae",
    icon: () => ICONS.uaeFlag,
    label: (t) => t.secondary_nav.contact_uae,
  },
  {
    id: "contact-now",
    href: () => "/products/contact-now",
    icon: () => ICONS.contactNow,
    label: (t) => t.secondary_nav.contact_now,
  },
  {
    id: "e-payment",
    href: () => "/e-payment",
    icon: () => ICONS.payment,
    label: (t) => t.secondary_nav.e_payments,
  },
  {
    id: "about-contact",
    section: "aboutContact",
    icon: (locale) => ICONS.brand(locale),
    label: (t) => t.secondary_nav.about_contact,
  },
  {
    id: "why-contact",
    href: () => "/why-contact",
    icon: (locale) => ICONS.brand(locale),
    label: (t) => t.secondary_nav.why_contact,
  },
  {
    id: "payment-channels",
    href: () => "/payment-channels",
    icon: (locale) => ICONS.brand(locale),
    label: (t) => t.secondary_nav.payment_channels,
  },
  {
    id: "blogs",
    href: () => "/blogs",
    icon: () => ICONS.blogs,
    label: (t) => t.secondary_nav.blog,
  },
  {
    id: "catalogue",
    href: () => CATALOGUE_URL,
    external: true,
    icon: (locale) => ICONS.brand(locale),
    label: (t) => t.secondary_nav.clients_program,
  },
  {
    id: "investor-relations",
    href: (locale) => investorRelationsDrawerUrl(locale),
    icon: (locale) => ICONS.brand(locale),
    label: (t) => t.secondary_nav.investor_relations,
  },
  {
    id: "branches",
    href: () => "/our-branches",
    icon: () => ICONS.marker,
    label: (t) => t.secondary_nav.branches_location,
  },
];

export const DRAWER_COMPANY_ITEMS = [
  {
    id: "financial",
    section: "financial",
    icon: () => ICONS.financial,
    label: (t) => t.secondary_nav.financial_services,
  },
  {
    id: "insurance",
    section: "insurance",
    icon: () => ICONS.insurance,
    label: (t) => t.secondary_nav.insurance_services,
  },
  {
    id: "business-investment",
    href: () => "/buisness-investment",
    icon: () => ICONS.investment,
    label: (t) => t.navbar_investment,
  },
  {
    id: "partnership",
    section: "partnership",
    icon: () => ICONS.partnership,
    label: (t) => t.navbar_partnership,
  },
];

/* Shown in the drawer only while signed in (individuals experience). */
export const DRAWER_ACCOUNT_ITEMS = [
  {
    id: "profile",
    href: "/profile",
    icon: ICONS.profile,
    label: (t) => t.userNav.profile,
  },
  {
    id: "contract",
    href: "/contract",
    icon: ICONS.installment,
    label: (t) => t.userNav.your_installement,
  },
  {
    id: "customerservices",
    href: "/customerservices",
    icon: ICONS.call,
    label: (t) => t.userNav.call_center,
  },
];

/* Routes worth having in the client router cache before the drawer is used. */
export const DRAWER_PREFETCH_PATHS = ["/contact-uae", "/login", "/profile"];
